// Guided ticket-create modal. Collects the location + system-type context that
// drives vendor/SLA routing (the thin old form only had title/desc/priority).
// Cascading Site→Building→Floor→Zone + optional Device/Asset + System type, with
// a LIVE routing preview ("→ <vendor> · SLA by <when>") via /tickets/resolve-vendor.
// On create the backend stamps the same vendor/contract/SLA.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  X, Sparkles, AlertTriangle, Ticket as TicketIcon, AlignLeft, Flag, Layers,
  MapPin, Building2, Building, Square, Cctv, HardDrive, Package, ShieldCheck, Siren,
  type LucideIcon,
} from 'lucide-react';
import { ticketingApi, type ResolvedRouting } from '@/lib/ticketing-api';
import { hierarchyApi, inventoryApi } from '@/lib/inventory-api';

// Small labelled field with a leading icon (visual consistency across the form).
function Field({ icon: Icon, label, children, className = '' }: { icon: LucideIcon; label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="flex items-center gap-1.5 text-xs font-medium text-muted"><Icon size={13} className="text-muted/70" /> {label}</span>
      {children}
    </label>
  );
}

const PRIORITY_TONE: Record<string, string> = { critical: 'text-critical', high: 'text-warning', medium: 'text-primary', low: 'text-muted' };
// Icon per system-type code (CCTV / access / alarm / other).
function systemTypeIcon(code: string): LucideIcon {
  if (/cctv|video|camera/i.test(code)) return Cctv;
  if (/access|door/i.test(code)) return ShieldCheck;
  if (/alarm|intrusion/i.test(code)) return Siren;
  return Layers;
}

export function NewTicketModal({ onClose, onCreated, initial }: {
  onClose: () => void;
  onCreated: () => void;
  /** Seed values (e.g. "Create ticket" from an asset's QR/detail page). */
  initial?: { title?: string; assetId?: string; siteId?: string; deviceId?: string; systemTypeId?: string };
}) {
  const { t } = useTranslation('components');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [systemTypeId, setSystemTypeId] = useState(initial?.systemTypeId ?? '');
  const [siteId, setSiteId] = useState(initial?.siteId ?? '');
  const [buildingId, setBuildingId] = useState('');
  const [floorId, setFloorId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [deviceId, setDeviceId] = useState(initial?.deviceId ?? '');
  const [assetId, setAssetId] = useState(initial?.assetId ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [routing, setRouting] = useState<ResolvedRouting | null | 'none'>(null);

  // Option sources.
  const { data: systemTypes } = useQuery({ queryKey: ['system-types'], queryFn: ticketingApi.listSystemTypes });
  const { data: sites } = useQuery({ queryKey: ['sites'], queryFn: ticketingApi.listSites });
  const { data: buildings } = useQuery({ queryKey: ['buildings', siteId], queryFn: () => hierarchyApi.buildings(siteId), enabled: !!siteId });
  const { data: floors } = useQuery({ queryKey: ['floors', buildingId], queryFn: () => hierarchyApi.floors(buildingId), enabled: !!buildingId });
  const { data: zones } = useQuery({ queryKey: ['zones', floorId], queryFn: () => hierarchyApi.zones(floorId), enabled: !!floorId });
  const { data: devices } = useQuery({ queryKey: ['inv-all'], queryFn: () => inventoryApi.list() });
  const { data: assets } = useQuery({ queryKey: ['assets'], queryFn: ticketingApi.listAssets });

  const siteDevices = (devices ?? []).filter((d) => !siteId || d.placement?.siteId === siteId);
  const siteAssets = (assets ?? []).filter((a) => !siteId || (a as { siteId?: string }).siteId === siteId);

  // Live routing preview whenever site + system-type (the routing inputs) change.
  useEffect(() => {
    if (!siteId && !deviceId) { setRouting(null); return; }
    let cancelled = false;
    ticketingApi.resolveVendor({ priority, systemTypeId: systemTypeId || undefined, siteId: siteId || undefined, buildingId: buildingId || undefined, floorId: floorId || undefined, zoneId: zoneId || undefined, deviceId: deviceId || undefined })
      .then((r) => { if (!cancelled) setRouting(r ?? 'none'); })
      .catch(() => { if (!cancelled) setRouting('none'); });
    return () => { cancelled = true; };
  }, [priority, systemTypeId, siteId, buildingId, floorId, zoneId, deviceId]);

  const vendorName = (id?: string | null) => (vendors ?? []).find((v) => v.id === id)?.name ?? id;
  const { data: vendors } = useQuery({ queryKey: ['vendors'], queryFn: ticketingApi.listVendors });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await ticketingApi.createTicket({
        title, description: description || undefined, priority,
        systemTypeId: systemTypeId || undefined, siteId: siteId || undefined,
        buildingId: buildingId || undefined, floorId: floorId || undefined, zoneId: zoneId || undefined,
        deviceId: deviceId || undefined, assetId: assetId || undefined,
      });
      onCreated();
    } catch {
      setErr(t('newTicketModal.errors.create'));
    } finally { setBusy(false); }
  };

  const sel = 'tk-input w-full py-1.5 text-sm';
  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <form className="tk-modal flex max-h-[88vh] w-full max-w-lg flex-col gap-3 overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-primary/15 text-primary"><TicketIcon size={16} /></span>
            {t('newTicketModal.title')}
          </h2>
          <button type="button" className="tk-btn-ghost px-2" onClick={onClose}><X size={16} /></button>
        </div>

        <Field icon={TicketIcon} label={t('newTicketModal.fields.title')}>
          <input className={sel} placeholder={t('newTicketModal.placeholders.title')} value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field icon={AlignLeft} label={t('newTicketModal.fields.description')}>
          <textarea className={sel} rows={2} placeholder={t('newTicketModal.placeholders.description')} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field icon={Flag} label={t('newTicketModal.fields.priority')}>
            <div className="relative">
              <span className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 ${PRIORITY_TONE[priority]}`}>●</span>
              <select className={`${sel} pl-7`} value={priority} onChange={(e) => setPriority(e.target.value)}>
                {['critical', 'high', 'medium', 'low'].map((p) => <option key={p} value={p}>{t(`newTicketModal.priority.${p}`)}</option>)}
              </select>
            </div>
          </Field>
          <Field icon={systemTypeIcon(systemTypeId)} label={t('newTicketModal.fields.systemType')}>
            <select className={sel} value={systemTypeId} onChange={(e) => setSystemTypeId(e.target.value)}>
              <option value="">{t('newTicketModal.options.select')}</option>
              {(systemTypes ?? []).map((s) => <option key={s.id} value={s.code}>{s.name}</option>)}
            </select>
          </Field>
        </div>

        {/* Cascading location */}
        <Field icon={MapPin} label={t('newTicketModal.fields.site')}>
          <select className={sel} value={siteId} onChange={(e) => { setSiteId(e.target.value); setBuildingId(''); setFloorId(''); setZoneId(''); setDeviceId(''); setAssetId(''); }}>
            <option value="">{t('newTicketModal.options.selectSite')}</option>
            {(sites ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        {siteId && (
          <div className="grid grid-cols-3 gap-2">
            <Field icon={Building2} label={t('newTicketModal.fields.building')}>
              <select className={sel} value={buildingId} onChange={(e) => { setBuildingId(e.target.value); setFloorId(''); setZoneId(''); }}>
                <option value="">{t('newTicketModal.options.any')}</option>
                {(buildings ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field icon={Building} label={t('newTicketModal.fields.floor')}>
              <select className={sel} value={floorId} onChange={(e) => { setFloorId(e.target.value); setZoneId(''); }} disabled={!buildingId}>
                <option value="">{t('newTicketModal.options.any')}</option>
                {(floors ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </Field>
            <Field icon={Square} label={t('newTicketModal.fields.zone')}>
              <select className={sel} value={zoneId} onChange={(e) => setZoneId(e.target.value)} disabled={!floorId}>
                <option value="">{t('newTicketModal.options.any')}</option>
                {(zones ?? []).map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
            </Field>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Field icon={HardDrive} label={t('newTicketModal.fields.device')}>
            <select className={sel} value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
              <option value="">—</option>
              {siteDevices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <Field icon={Package} label={t('newTicketModal.fields.asset')}>
            <select className={sel} value={assetId} onChange={(e) => setAssetId(e.target.value)}>
              <option value="">—</option>
              {siteAssets.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.category}</option>)}
            </select>
          </Field>
        </div>

        {/* Live routing preview */}
        {routing && routing !== 'none' && (
          <div className="flex items-start gap-2 rounded-md bg-primary/10 p-2.5 text-xs text-primary ring-1 ring-primary/20">
            <Sparkles size={14} className="mt-0.5 shrink-0" />
            <span>{t('newTicketModal.routing.handledByPrefix')} <b>{vendorName(routing.vendorId)}</b>{routing.contractId ? t('newTicketModal.routing.underContract') : t('newTicketModal.routing.assigned')}{routing.slaDeadline ? t('newTicketModal.routing.slaResponseBy', { time: new Date(routing.slaDeadline).toLocaleString() }) : ''}.</span>
          </div>
        )}
        {routing === 'none' && (siteId || deviceId) && (
          <div className="flex items-start gap-2 rounded-md bg-warning/10 p-2.5 text-xs text-warning ring-1 ring-warning/20">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{t('newTicketModal.routing.noCoveragePrefix')} <b>{t('newTicketModal.routing.unassigned')}</b>{t('newTicketModal.routing.noCoverageSuffix')}</span>
          </div>
        )}

        {err && <div className="text-xs text-critical">{err}</div>}
        <div className="flex justify-end gap-2 border-t border-white/10 pt-3">
          <button type="button" className="tk-btn-ghost" onClick={onClose}>{t('newTicketModal.actions.cancel')}</button>
          <button type="submit" className="tk-btn-primary" disabled={busy || !title}>{busy ? t('newTicketModal.actions.creating') : t('newTicketModal.actions.create')}</button>
        </div>
      </form>
    </div>
  );
}
